import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import { Trash2, Minus, Plus } from 'lucide-react';
import { resolveImageUrl } from '../lib/imageUrl';

const CartPage = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { cartItems, updateQuantity, removeFromCart, getCartTotal } = useCart();
  const { subtotal, shipping, total } = getCartTotal();

  const amountUntilFreeShipping = Math.max(0, 200 - subtotal);

  if (cartItems.length === 0) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <p className="text-muted-foreground mb-4">{t('emptyCart')}</p>
        <Button onClick={() => navigate('/shop')} data-testid="go-to-shop">
          {t('continueShopping')}
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 lg:px-12 py-8">
      <h1 className="text-3xl md:text-4xl font-heading font-bold mb-8 uppercase" data-testid="cart-title">
        {t('cart')}
      </h1>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {cartItems.map((item) => (
            <div
              key={item.product.id}
              className="flex flex-col sm:flex-row gap-4 bg-white border border-border rounded-md p-4"
              data-testid={`cart-item-${item.product.id}`}
            >
              <div className="flex gap-4 flex-1">
                <img
                  src={resolveImageUrl(item.product.image_url)}
                  alt={item.product.name}
                  className="w-20 h-20 sm:w-24 sm:h-24 object-contain rounded flex-shrink-0 bg-white border"
                  loading="lazy"
                />

                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-semibold text-base sm:text-lg mb-1 truncate">{item.product.name}</h3>
                  <p className="text-lg sm:text-xl font-bold">€{item.product.price.toFixed(2)}</p>
                </div>
              </div>

              <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4">
                <div className="flex items-center border border-border rounded-md">
                  <button
                    onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                    className="p-2 hover:bg-muted"
                    data-testid={`decrease-quantity-${item.product.id}`}
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="px-3 sm:px-4 font-semibold" data-testid={`quantity-${item.product.id}`}>{item.quantity}</span>
                  <button
                    onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                    className="p-2 hover:bg-muted"
                    data-testid={`increase-quantity-${item.product.id}`}
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>

                <button
                  onClick={() => removeFromCart(item.product.id)}
                  className="p-2 hover:bg-red-50 rounded flex-shrink-0"
                  data-testid={`remove-item-${item.product.id}`}
                >
                  <Trash2 className="h-5 w-5 text-red-500" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-1">
          <div className="bg-muted p-6 rounded-md">
            <h2 className="text-xl font-heading font-bold mb-4">{t('total')}</h2>

            <div className="space-y-3 mb-4">
              <div className="flex justify-between">
                <span>Zwischensumme (netto)</span>
                <span className="font-semibold" data-testid="subtotal-net-amount">€{(subtotal / 1.19).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>MwSt. (19%)</span>
                <span className="font-semibold" data-testid="vat-amount">€{(subtotal - subtotal / 1.19).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>{t('subtotal') || 'Zwischensumme (brutto)'}</span>
                <span className="font-semibold" data-testid="subtotal-amount">€{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>{t('shipping')}</span>
                <span className="font-semibold" data-testid="shipping-amount">
                  {shipping === 0 ? t('freeShipping') : `€${shipping.toFixed(2)}`}
                </span>
              </div>
              <div className="border-t border-border pt-3 flex justify-between text-lg">
                <span className="font-bold">{t('total')}</span>
                <span className="font-bold" data-testid="total-amount">€{total.toFixed(2)}</span>
              </div>
            </div>

            {amountUntilFreeShipping > 0 && (
              <div className="mb-4 p-3 bg-gold/10 border border-gold rounded text-sm" data-testid="free-shipping-banner">
                {t('remainingForFreeShipping').replace('{amount}', `€${amountUntilFreeShipping.toFixed(2)}`)}
              </div>
            )}

            <Button
              onClick={() => navigate('/checkout')}
              className="w-full h-12 text-lg bg-gold hover:bg-gold/90 text-primary font-heading"
              data-testid="checkout-button"
            >
              {t('checkout')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;